import { PropsWithChildren, useContext, useMemo } from 'react'

import {
  FirmFragment,
  useFetchFirm,
  useFetchUserFeatureFlags,
} from '~/graphql/generated/query.types'
import { FirmContext } from '~/util/FirmContext'
import { UserContext } from '~/util/UserContext'

import { usePusherSubscribe } from './hooks/usePusherSubscribe'
import { useThrottledPokeRefetch } from './hooks/useThrottledPokeRefetch'

export function FirmWrapper({ children }: PropsWithChildren<unknown>) {
  const { loading, data, refetch, startPolling, stopPolling } = useFetchFirm()
  const { loading: loadingFFs } = useFetchUserFeatureFlags()
  const user = useContext(UserContext)
  const firm = data?.firm ?? null

  // The FIRM channel is poked on every job completion firm-wide, so during pricing/sync runs
  // pokes arrive in rapid bursts and FetchFirm is a large payload; see useThrottledPokeRefetch.
  const onPoke = useThrottledPokeRefetch(refetch)
  usePusherSubscribe('FIRM', user.firmSid, onPoke)

  // Memoize with stable identities (Apollo returns the same firm object when a refetch yields
  // unchanged data), so that FetchFirm refetches that change nothing don't re-render every
  // useFirm consumer in the app. The cast covers the pre-load render where firm is still null;
  // the provider is only rendered once firm is loaded.
  const contextValue = useMemo(
    () =>
      [firm as FirmFragment, refetch, startPolling, stopPolling] satisfies React.ContextType<
        typeof FirmContext
      >,
    [firm, refetch, startPolling, stopPolling]
  )

  if (loading || loadingFFs || !firm) {
    return null
  }

  return <FirmContext.Provider value={contextValue}>{children}</FirmContext.Provider>
}
